// src/hooks/useBusinessVerification.ts
import { useState, useCallback } from "react";
import axios from "../config/axiosConfig.ts";
import { BaseUrl } from "../const.ts";

interface CardRegistrationResponse {
  code: number;
  message: string;
  body: string; // redirect URL
}

interface ApiPartner {
  cardId: string;
  userName: string;
  status: string | null;
  cardMask: string | null;
  createdAt: string;
}

export default function useBusinessVerification() {
  // For registration (POST)
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // For fetching registered cards (GET)
  const [cards, setCards] = useState<ApiPartner[]>([]);
  const [loading, setLoading] = useState(false);         // For card fetch
  const [cardsError, setCardsError] = useState<Error | null>(null);

  // Fetch successful cards, ordered DESC
  const fetchCards = useCallback(async () => {
    setLoading(true);
    setCardsError(null);
    try {
      const { data } = await axios.get<{ code: number; message: string; body: ApiPartner[] }>(
          `${BaseUrl}/user/user/registered-cards`
      );
      const filtered = data.body
          .filter(card => card.status === "success")
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setCards(filtered);
    } catch (err: any) {
      setCardsError(new Error(err.response?.data?.message || err.message || "Xəta baş verdi"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Card registration (POST)
  const verify = async (businessName: string) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);
    setIsSuccess(false);
    setRedirectUrl(null);

    try {
      const url = `${BaseUrl}/user/user/card-registration?userName=${encodeURIComponent(
          businessName
      )}`;

      const { data } = await axios.post<CardRegistrationResponse>(url);

      if (data.code === 301) {
        setIsSuccess(true);
        setRedirectUrl(data.body);
      } else {
        throw new Error(data.message || "Card registration failed");
      }
    } catch (err: any) {
      setIsError(true);
      setError(new Error(err.response?.data?.message || err.message || "Xəta baş verdi"));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Registration logic
    verify,
    isLoading,
    isError,
    error,
    isSuccess,
    redirectUrl,

    // Card list logic
    cards,
    loading,
    fetchCards,
    cardsError,
  };
}
